import { Option, Select } from 'app/components/fields/Select';
import type { Experience } from 'app/typings/experiences';
import type { Space } from 'app/typings/hotels/Space/Space';
import type { SpaceType } from 'app/typings/hotels/SpaceType/SpaceType';
import { getSpaceTypeDisplayName } from 'app/utils/hotels';

export const NOT_APPLICABLE_SPACE_ID = -1;

export const getSpaceLabel = (
  space: Space,
  spaceTypes: SpaceType[]
): string => {
  const spaceType = spaceTypes.find((type) => type.id === space.spaceTypeId);
  const spaceTypeName = getSpaceTypeDisplayName(spaceType?.name);
  const spaceName = space.name ? ` (${space.name})` : '';

  return `${spaceTypeName}${spaceName}`;
};

type SpacesSelectProps = {
  value?: number[] | null;
  onChange?: (newValue: number[]) => void;
  hotelSpaces: Space[];
  spaceTypes: SpaceType[];
  mode?: 'view' | 'edit';
  notApplicableOption?: boolean;
  setFieldsValue: (values: Partial<Experience>) => void;
  id?: string;
};

export const SpacesSelect = ({
  value,
  onChange,
  hotelSpaces,
  spaceTypes,
  mode,
  notApplicableOption = false,
  setFieldsValue,
  id,
}: SpacesSelectProps) => {
  const safeValue = value ?? [];

  const displayNotApplicableOption =
    notApplicableOption &&
    (safeValue.length === 0 || safeValue.includes(NOT_APPLICABLE_SPACE_ID));

  const handleSelect = (selected: number) => {
    if (
      selected !== NOT_APPLICABLE_SPACE_ID &&
      safeValue.includes(NOT_APPLICABLE_SPACE_ID)
    ) {
      setFieldsValue({
        spaceIds: [
          // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
          ...safeValue.filter((id) => id !== NOT_APPLICABLE_SPACE_ID),
          selected,
        ],
      });
    }
  };

  const handleChange = (newValue: number[]) => {
    onChange?.(newValue);
  };

  return (
    <Select
      id={id}
      value={safeValue}
      onSelect={handleSelect}
      onChange={handleChange}
      mode="multiple"
      allowClear
      showSearch
      showArrow
      disabled={mode === 'view'}
    >
      {hotelSpaces.map((space) => (
        <Option key={space.id} value={space.id}>
          {getSpaceLabel(space, spaceTypes)}
        </Option>
      ))}
      {displayNotApplicableOption && (
        <Option value={NOT_APPLICABLE_SPACE_ID}>Not applicable</Option>
      )}
    </Select>
  );
};
